import { useState } from 'react';
import {
    Modal,
    ModalBody,
    Button,
    Inline,
    Stack,
    Text
} from '@7shifts/sous-chef-base';
import type {
    HealthCheckRequiredResponse,
    HealthCheckQuestionnaire,
    HealthCheckAnswer,
    PunchResponse
} from '@src/api/punchTypes';
import { useLocale, useTranslation } from '@src/i18n';
import { HealthCheckIcon } from './icons/HealthCheckIcon';
import './HealthCheckModal.css';

function resolveQuestionnaire(
    questionnaires: HealthCheckQuestionnaire[],
    locale: string
): HealthCheckQuestionnaire | undefined {
    if (!questionnaires.length) return undefined;
    return (
        questionnaires.find(q => q.language === locale) ??
        questionnaires.find(q => q.language.startsWith(locale.split('-')[0])) ??
        questionnaires[0]
    );
}

interface HealthCheckModalProps {
    response: HealthCheckRequiredResponse;
    onExecute: (execute: () => Promise<PunchResponse>) => void;
    onDismiss: () => void;
}

export function HealthCheckModal({
    response,
    onExecute,
    onDismiss
}: HealthCheckModalProps) {
    const locale = useLocale();
    const { t } = useTranslation();
    const questionnaire = resolveQuestionnaire(response.questionnaires, locale);
    const [answers, setAnswers] = useState<Record<number, number>>({});

    if (!questionnaire) {
        return (
            <Modal
                header={t('healthCheck.unavailableTitle')}
                testId="health-check-modal"
            >
                <ModalBody>
                    <Stack space={16}>
                        <Text as="body">
                            {t('healthCheck.unavailableBody')}
                        </Text>
                        <Button
                            type="button"
                            theme="primary"
                            size="full-width"
                            onClick={onDismiss}
                            testId="health-check-dismiss"
                        >
                            {t('healthCheck.dismiss')}
                        </Button>
                    </Stack>
                </ModalBody>
            </Modal>
        );
    }

    const allAnswered = questionnaire.questions.every(
        q => answers[q.id] !== undefined
    );

    function handleSelect(questionId: number, responseId: number) {
        setAnswers(prev => ({ ...prev, [questionId]: responseId }));
    }

    function handleSubmit() {
        const collected: HealthCheckAnswer[] = questionnaire!.questions.map(
            q => ({
                questionId: q.id,
                responseId: answers[q.id]!
            })
        );
        onExecute(() => response.submitResponses(collected));
    }

    return (
        <Modal testId="health-check-modal">
            <ModalBody>
                <Stack space={16}>
                    <Stack space={8} alignItems="center">
                        <div
                            className="health-check-modal__icon"
                            aria-hidden="true"
                        >
                            <HealthCheckIcon />
                        </div>
                        <Text as="h2" alignment="center">
                            {questionnaire.title}
                        </Text>
                    </Stack>
                    {questionnaire.questions.map(question => (
                        <Stack key={question.id} space={8}>
                            <Text
                                as="body"
                                testId={`hc-question-${question.id}`}
                            >
                                {question.text}
                            </Text>
                            <Inline space={8} flex={[1, 1]}>
                                {question.responses.map(option => (
                                    <Button
                                        key={option.id}
                                        type="button"
                                        theme={
                                            answers[question.id] === option.id
                                                ? 'primary'
                                                : 'hollow'
                                        }
                                        size="full-width"
                                        onClick={() =>
                                            handleSelect(question.id, option.id)
                                        }
                                        testId={`hc-option-${question.id}-${option.id}`}
                                    >
                                        {option.text}
                                    </Button>
                                ))}
                            </Inline>
                        </Stack>
                    ))}
                    <Stack space={8}>
                        <Button
                            type="button"
                            theme="primary"
                            size="full-width"
                            disabled={!allAnswered}
                            onClick={handleSubmit}
                            testId="health-check-submit"
                        >
                            {questionnaire.buttonText ||
                                t('healthCheck.submitFallback')}
                        </Button>
                        <Button
                            type="button"
                            theme="hollow"
                            size="full-width"
                            onClick={onDismiss}
                            testId="health-check-cancel"
                        >
                            {t('common.cancel')}
                        </Button>
                    </Stack>
                </Stack>
            </ModalBody>
        </Modal>
    );
}
